export const grayColors = {
  0: '#FFFFFF',
  100: '#F7F9FA',
  200: '#EDF0F2',
  300: '#E3E7EB',
  400: '#C9CFD8',
  500: '#AEB7C3',
  600: '#93A0AF',
  700: '#77879C',
  800: '#617087',
  900: '#4A5A70',
  1000: '#354358',
  1100: '#091529'
} as const;

export const blueColors = {
  100: '#EAF0F9',
  200: '#D6E2F2',
  300: '#ACC4E5',
  400: '#83A7D9',
  500: '#5989CC',
  600: '#3E70B5',
  700: '#265699',
  800: '#1D4173',
  900: '#132B4C'
} as const;

export const greenColors = {
  100: '#EFFAF5',
  200: '#DFF4EB',
  300: '#BFE9D8',
  400: '#A0DFC4',
  500: '#80D4B1',
  600: '#60C99D',
  700: '#4DA17E'
} as const;

export const redColors = {
  100: '#FAEEEE',
  200: '#F5DCDC',
  300: '#EBBABA',
  400: '#E09797',
  500: '#D67575',
  600: '#CC5252',
  700: '#BF3939'
} as const;

export const yellowColors = {
  100: '#FFFAF2',
  200: '#FFF4E4',
  300: '#FFEACA',
  400: '#FFDFAF',
  500: '#FFD595',
  600: '#FFCA7A',
  700: '#FFBD59'
} as const;

export const orangeColors = {
  100: '#F1A47A',
  200: '#E9865D',
  300: '#E66B43',
  400: '#C95028'
} as const;

export const aiColors = {
  'nav-action': '#2F5FBD',
  'nav-hairline': '#DCABE9',
  'nav-accent-start': '#D3DBED',
  'nav-accent-end': '#E5D0EB',
  'surface-gradient-start': 'rgba(35, 80, 168, 0.2)',
  'surface-gradient-end': 'rgba(130, 22, 157, 0.2)'
} as const;

export const spacing = {
  0: '0',
  px: '1px',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  11: '2.75rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  28: '7rem',
  32: '8rem',
  36: '9rem',
  40: '10rem',
  44: '11rem',
  48: '12rem'
} as const;

export const borderRadius = {
  none: '0',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  xxl: '1rem',
  xxxl: '1.5rem',
  full: '100%'
} as const;

export const shadows = {
  none: 'none',
  base: '0px 1px 5px 0px rgba(53, 67, 88, 0.05)',
  hover: '0px 8px 20px 0px rgba(53, 67, 88, 0.08)',
  modal: '0px 20px 34px 0px rgba(53, 67, 88, 0.08)',
  popover: '0px 4px 18px 0px rgba(53, 67, 88, 0.08)',
  sm: '0px 1px 2px 0px rgba(53, 67, 88, 0.05)',
  md: '0px 4px 6px -1px rgba(53, 67, 88, 0.08), 0px 2px 4px -1px rgba(53, 67, 88, 0.04)',
  lg: '0px 10px 15px -3px rgba(53, 67, 88, 0.08), 0px 4px 6px -2px rgba(53, 67, 88, 0.04)',
  xl: '0px 20px 25px -5px rgba(53, 67, 88, 0.08), 0px 10px 10px -5px rgba(53, 67, 88, 0.03)',
  '2xl': '0px 25px 50px -12px rgba(53, 67, 88, 0.15)',
  inner: 'inset 0px 2px 4px 0px rgba(53, 67, 88, 0.05)',
  focusRing: '0 0 0 0.2rem'
} as const;

export const fontFamily = {
  sans: '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  icon: '"Material Symbols Outlined"'
} as const;

export const fontSize = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem'
} as const;

export const fontWeight = {
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700'
} as const;

export const lineHeight = {
  none: '1',
  tight: '1.25',
  snug: '1.375',
  normal: '1.5',
  relaxed: '1.625',
  loose: '2',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem'
} as const;

export const letterSpacing = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em'
} as const;

export const zIndex = {
  auto: 'auto',
  0: '0',
  10: '10',
  20: '20',
  30: '30',
  40: '40',
  50: '50',
  content: '1',
  ui: '200',
  sticky: '400',
  dropdown: '500',
  overlay: '750',
  popover: '1025',
  tooltip: '1050',
  modal: '2500',
  notification: '3500',
  debug: '9500'
} as const;

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
  '3xl': '1920px'
} as const;

export const transition = {
  duration: {
    fastest: '50ms',
    faster: '100ms',
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms',
    slowest: '700ms'
  },
  timing: {
    linear: 'linear',
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out'
  }
} as const;

export const opacity = {
  0: '0',
  5: '0.05',
  10: '0.1',
  20: '0.2',
  25: '0.25',
  30: '0.3',
  40: '0.4',
  50: '0.5',
  60: '0.6',
  70: '0.7',
  75: '0.75',
  80: '0.8',
  90: '0.9',
  95: '0.95',
  100: '1'
} as const;

export const primitiveTokens = {
  gray: grayColors,
  blue: blueColors,
  green: greenColors,
  red: redColors,
  yellow: yellowColors,
  orange: orangeColors,
  ai: aiColors,

  spacing,
  borderRadius,
  shadows,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  zIndex,
  breakpoints,
  transition,
  opacity
};
